import i18n from '@i18next/i18n';
import { ActionType } from '@editor/src/js/constants/common-action-type';
import { getBorderStyleData } from '@editor/src/js/constants/toolbar-data';
import { CHART_BAR_SERIES_OPTIONS, CHART_BUBBLIE_SERIES_OPTIONS, CHART_PIE_DOUGHNUT_SERIES_OPTIONS, SLIDE_FORM_SCHEMA } from '@tencent/chart-drawer-components/lib/schema';
import { FormLabelName, FormItemName, FormPanelName } from '../drawer/constants';
import {
    AlignType,
    IndentType,
} from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/components/text-editor/editor-controller/editor-operations/constant';
import { SHAPE_FILL_SCHEMA, SHAPE_BORDER_SCHEMA, SHAPE_EFFECT_SCHEMA } from '@tencent/drawing-drawer-components/lib/schema';

const fillCommonItems = [
    ...SHAPE_FILL_SCHEMA.items,
    FormItemName.SHOW_MASTER_SHAPE,
    [FormItemName.RESET_BACKGROUND, FormItemName.SPACE, FormItemName.APPLY_TO_ALL_SLIDE],
];

const masterBackgroundSchema =  {
    label: FormLabelName.BACKGROUND,
    key: FormItemName.FILL_PRESET,
    collapsable: true,
    items: [FormItemName.FONT_THEME, ...fillCommonItems],
};

const fillSchema = {
    label: FormLabelName.FILL,
    key: FormItemName.FILL_PRESET,
    collapsable: true,
    items: fillCommonItems,
};

const masterEditSchema = [
    {
        label: FormLabelName.LAYOUT,
        key: FormItemName.LAYOUT,
        collapsable: true,
        items: [
            FormItemName.PLACEHOLDER_FORMAT,
        ],
    },
    masterBackgroundSchema,
    {
        label: FormLabelName.SLIDE_SIZE,
        key: FormItemName.SLIDE_SIZE,
        collapsable: true,
        items: [
            FormItemName.SLIDE_SIZE_SELECT,
        ],
    },
];

const fillAndBorderSchema = [
    fillSchema,
    SHAPE_BORDER_SCHEMA,
];

const effectSchema = [
    ...SHAPE_EFFECT_SCHEMA,
    {
        label: FormLabelName.THREE_D_PROPERTIES,
        key: FormItemName.RESET_3D,
        collapsable: true,
        items: [
            FormItemName.STYLE_3D,
            [FormItemName.DEPTH_3D, FormItemName.SCALE_DEPTH_3D],
            [FormItemName.DEPTH_COLOR_3D, FormItemName.CHANGE_DEPTH_COLOR_3D],
            FormItemName.ROTATION,
            [FormItemName.CAMERA_PRESET_3D, FormItemName.CHANGE_CAMERA_PRESET],
            [FormItemName.ANGLE_X, FormItemName.LAYOUT_ROTATE_3D_X],
            [FormItemName.ANGLE_Y, FormItemName.LAYOUT_ROTATE_3D_Y],
            [FormItemName.ANGLE_Z, FormItemName.LAYOUT_ROTATE_3D_Z],
            FormItemName.SHOW_FLAT_TEXT,
            FormItemName.LIGHT_3D,
            [FormItemName.LIGHT_TYPE_3D, FormItemName.CHANGE_3D_LIGHT_TYPE],
            [FormItemName.LIGHT_ROTATION, FormItemName.CHANGE_LIGHT_ROTATION_ANGLE],
            FormItemName.MATERIAL_3D,
            [FormItemName.MATERIAL_TYPE, FormItemName.CHANGE_MATERIAL_TYPE],
            // TODO: @oddguan 棱台还有渲染bug，暂时不开放入口
            // FormItemName.FRUSTUM_3D,
            // [FormItemName.FRONT_FRUSTUM_3D_HEIGHT, FormItemName.SCALE_FRONT_FRUSTUM_3D_HEIGHT],
            // [FormItemName.FRONT_FRUSTUM_3D_WIDTH, FormItemName.SCALE_FRONT_FRUSTUM_3D_WIDTH],
            // [FormItemName.BACK_FRUSTUM_3D_HEIGHT, FormItemName.SCALE_BACK_FRUSTUM_3D_HEIGHT],
            // [FormItemName.BACK_FRUSTUM_3D_WIDTH, FormItemName.SCALE_BACK_FRUSTUM_3D_WIDTH],
        ],
    },
];

const sizeAndPositionSchema = [
    {
        label: FormLabelName.SIZE,
        collapsable: true,
        items: [
            [FormItemName.HEIGHT, FormItemName.SPACE, FormItemName.WIDTH],
            [FormItemName.SCALE_HEIGHT, FormItemName.SPACE, FormItemName.SCALE_WIDTH],
            FormItemName.SIZE_ADJUST, FormItemName.RELATIVE_IMAGE_ORIGINAL_SIZE, FormItemName.BEST_PLAY_SCALE,
            [FormItemName.RESOLUTION_TEXT, FormItemName.RESOLUTION],
            FormItemName.ORIGINAL_SIZE, FormItemName.ORIGINAL_SIZE_CONTENT, FormItemName.RESET_ORIGINAL_SIZE,
        ],
    },
    {
        label: FormLabelName.POSITION,
        collapsable: true,
        items: [
            FormItemName.HORIZONTAL_POSITION_TEXT,
            [FormItemName.HORIZONTAL_POSITION, FormItemName.SPACE, FormItemName.HORIZONTAL_FROM],
            FormItemName.VERTICAL_POSITION_TEXT,
            [FormItemName.VERTICAL_POSITION, FormItemName.SPACE, FormItemName.VERTICAL_FROM],
        ],
    },
    {
        label: FormLabelName.ROTATE,
        collapsable: true,
        items: [
            FormItemName.LAYOUT_ROTATE,
        ],
    },
];

const layoutSchemaWithoutThreeD = [
    {
        label: FormLabelName.ALIGN,
        collapsable: true,
        items: [
            [
                ActionType.ALIGN_HORIZONTALLT_LEFT, ActionType.ALIGN_HORIZONTALLT_CENTER,
                ActionType.ALIGN_HORIZONTALLT_RIGHT, ActionType.ALIGN_VERTICALLY_TOP,
                ActionType.ALIGN_VERTICALLY_CENTER, ActionType.ALIGN_VERTICALLY_BOTTOM,
            ],
            [ActionType.ALIGN_DISTRIBUTE_HORIZONTALLT, FormItemName.SPACE, ActionType.ALIGN_DISTRIBUTE_VERTICALLY],
        ],
    },
    {
        label: FormLabelName.LEVEL,
        collapsable: true,
        key: FormItemName.SELECT_WINDOW,
        items: [
            [ActionType.MOVETOP, ActionType.MOVEBOTTOM, FormItemName.SPACE, ActionType.MOVEUP, ActionType.MOVEDOWN],
        ],
    },
    {
        label: FormLabelName.GROUP,
        collapsable: true,
        items: [
            [ActionType.ADDGROUP, FormItemName.SPACE, ActionType.REMOVEGROUP],
        ],
    },
    {
        label: FormLabelName.SIZE,
        collapsable: true,
        items: [
            [FormItemName.HEIGHT, FormItemName.SPACE, FormItemName.WIDTH],
            [FormItemName.SCALE_HEIGHT, FormItemName.SPACE, FormItemName.SCALE_WIDTH],
            FormItemName.SIZE_ADJUST, FormItemName.RELATIVE_IMAGE_ORIGINAL_SIZE, FormItemName.BEST_PLAY_SCALE,
            [FormItemName.RESOLUTION_TEXT, FormItemName.RESOLUTION],
            FormItemName.ORIGINAL_SIZE, FormItemName.ORIGINAL_SIZE_CONTENT, FormItemName.RESET_ORIGINAL_SIZE,
        ],
    },
    {
        label: FormLabelName.POSITION,
        collapsable: true,
        items: [
            FormItemName.HORIZONTAL_POSITION_TEXT,
            [FormItemName.HORIZONTAL_POSITION, FormItemName.SPACE, FormItemName.HORIZONTAL_FROM],
            FormItemName.VERTICAL_POSITION_TEXT,
            [FormItemName.VERTICAL_POSITION, FormItemName.SPACE, FormItemName.VERTICAL_FROM],
        ],
    },
    {
        label: FormLabelName.ROTATE,
        collapsable: true,
        items: [
            FormItemName.LAYOUT_ROTATE,
        ],
    },
];

// smartart 屏蔽旋转
const layoutSchemaWithoutThreeDAndRotate = [
    {
        label: FormLabelName.ALIGN,
        collapsable: true,
        items: [
            [
                ActionType.ALIGN_HORIZONTALLT_LEFT, ActionType.ALIGN_HORIZONTALLT_CENTER,
                ActionType.ALIGN_HORIZONTALLT_RIGHT, ActionType.ALIGN_VERTICALLY_TOP,
                ActionType.ALIGN_VERTICALLY_CENTER, ActionType.ALIGN_VERTICALLY_BOTTOM,
            ],
            [ActionType.ALIGN_DISTRIBUTE_HORIZONTALLT, FormItemName.SPACE, ActionType.ALIGN_DISTRIBUTE_VERTICALLY],
        ],
    },
    {
        label: FormLabelName.LEVEL,
        collapsable: true,
        key: FormItemName.SELECT_WINDOW,
        items: [
            [ActionType.MOVETOP, ActionType.MOVEBOTTOM, FormItemName.SPACE, ActionType.MOVEUP, ActionType.MOVEDOWN],
        ],
    },
    {
        label: FormLabelName.GROUP,
        collapsable: true,
        items: [
            [ActionType.ADDGROUP, FormItemName.SPACE, ActionType.REMOVEGROUP],
        ],
    },
    {
        label: FormLabelName.SIZE,
        collapsable: true,
        items: [
            [FormItemName.HEIGHT, FormItemName.SPACE, FormItemName.WIDTH],
            [FormItemName.SCALE_HEIGHT, FormItemName.SPACE, FormItemName.SCALE_WIDTH],
            FormItemName.SIZE_ADJUST, FormItemName.RELATIVE_IMAGE_ORIGINAL_SIZE, FormItemName.BEST_PLAY_SCALE,
            [FormItemName.RESOLUTION_TEXT, FormItemName.RESOLUTION],
            FormItemName.ORIGINAL_SIZE, FormItemName.ORIGINAL_SIZE_CONTENT, FormItemName.RESET_ORIGINAL_SIZE,
        ],
    },
    {
        label: FormLabelName.POSITION,
        collapsable: true,
        items: [
            FormItemName.HORIZONTAL_POSITION_TEXT,
            [FormItemName.HORIZONTAL_POSITION, FormItemName.SPACE, FormItemName.HORIZONTAL_FROM],
            FormItemName.VERTICAL_POSITION_TEXT,
            [FormItemName.VERTICAL_POSITION, FormItemName.SPACE, FormItemName.VERTICAL_FROM],
        ],
    },
];


const layoutSchema = [
    ...layoutSchemaWithoutThreeD,
];

const pictureSchema = [
    {
        label: FormLabelName.PICTURE_CROP,
        collapsable: true,
        items: [
            FormItemName.PICTURE_TAILOR,
            FormItemName.PICTURE_POSITION_TEXT,
            [FormItemName.PICTURE_POSITIOIN_WIDTH, FormItemName.SPACE, FormItemName.PICTURE_POSITIOIN_HEIGHT],
            [FormItemName.PICTURE_POSITIOIN_OFFSET_X, FormItemName.SPACE, FormItemName.PICTURE_POSITIOIN_OFFSET_Y],
            FormItemName.CROP_POSITION_TEXT,
            [FormItemName.CROP_POSITIOIN_WIDTH, FormItemName.SPACE, FormItemName.CROP_POSITIOIN_HEIGHT],
            [FormItemName.CROP_POSITIOIN_LEFT, FormItemName.SPACE, FormItemName.CROP_POSITIOIN_TOP],
        ],
    },
    {
        label: FormLabelName.PICTURE_OPACITY,
        collapsable: true,
        items: [
            FormItemName.PICTURE_OPACITY,
        ],
    },
    {
        label: FormLabelName.PICTURE_FIX,
        key: FormItemName.PICTURE_FIX_RESET,
        collapsable: true,
        items: [
            FormItemName.SHARPEN_SOFTEN,
            FormItemName.BRIGHTNESS,
            FormItemName.CONSTRAST,
        ],
    },
    {
        label: FormLabelName.PICTURE_EFFECT,
        key: FormItemName.PICTURE_COLOR_RESET,
        collapsable: true,
        items: [
            FormItemName.SATURATE,
            FormItemName.COLOR_TEMPERATURE,
            [FormItemName.PICTURE_RECOLOR_TEXT, FormItemName.PICTURE_RECOLOR],
        ],
    },
];

const textSchema = [
    {
        label: FormLabelName[ActionType.FONT],
        collapsable: true,
        items: [
            [FormItemName.FONT, FormItemName.PUSHER, FormItemName.FONT_COLOR],
            [FormItemName.FONT_SIZE, FormItemName.SPACE, ActionType.FONTSIZE_UP,
                ActionType.FONTSIZE_DOWN, FormItemName.SPACE, FormItemName.FONT_BACK_COLOR],
            [ActionType.BOLD, ActionType.ITALIC, ActionType.UNDERLINE, ActionType.DELETELINE,
                ActionType.SUPERSCRIPT, ActionType.SUBSCRIPT],
        ],
    },
    {
        label: FormLabelName.PARAGRAPH,
        collapsable: true,
        items: [
            [
                AlignType.HORIZONTAL_LEFT, AlignType.HORIZONTAL_CENTER,
                AlignType.HORIZONTAL_RIGHT, AlignType.HORIZONTAL_JUSTIFY, AlignType.HORIZONTAL_DISTRIBUTE,
            ],
            [
                AlignType.VERTICAL_TOP, AlignType.VERTICAL_CENTER, AlignType.VERTICAL_BOTTOM,
                FormItemName.SPACE, IndentType.INDENT_REDUCE, IndentType.INDENT_INCREASE,
            ],
            [
                FormItemName.ITEM_NUMBER, FormItemName.SPACE, FormItemName.DIGIT_NUMBER,
                FormItemName.SPACE, FormItemName.ROW_SPACE,
            ],
        ],
    },
    {
        label: FormLabelName.TEXTBOX,
        collapsable: true,
        items: [
            FormItemName.FONT_ADJUST_TEXT,
            FormItemName.FONT_ADJUST,
            FormItemName.TEXT_DIRECTION_LABEL,
            FormItemName.TEXT_DIRECTION,
            FormItemName.ORDER_OF_LINE_LABEL,
            FormItemName.ORDER_OF_LINE,
            [FormItemName.FONT_PADDING_TEXT, FormItemName.PUSHER, FormItemName.FONT_PADDING_UNIT],
            [FormItemName.FONT_PADDING_LEFT, FormItemName.SPACE, FormItemName.FONT_PADDING_RIGHT, FormItemName.SPACE,
                FormItemName.FONT_PADDING_TOP, FormItemName.SPACE, FormItemName.FONT_PADDING_BOTTOM],
            FormItemName.FONT_AUTO_BREAK,
        ],
    },
    {
        label: FormLabelName[ActionType.FONT_FILL],
        key: FormItemName.FONT_FILL_PRESET,
        items: [
            FormItemName.FONT_FILL_SELECT,
            FormItemName.FONT_SOLID_FILL_COLOR,
            FormItemName.FONT_SOLID_FILL_OPACITY,
            [
                FormItemName.FONT_GRADUAL_FILL_TYPE_TEXT, FormItemName.PUSHER,
                FormItemName.FONT_GRADUAL_FILL_LINEAR_GRADIENT, FormItemName.FONT_GRADUAL_FILL_RADIAL_GRADIENT,
                FormItemName.FONT_GRADUAL_FILL_RECT_GRADIENT, FormItemName.FONT_GRADUAL_FILL_SHAPE_GRADIENT,
            ],
            [FormItemName.FONT_GRADUAL_FILL_DIRECTION_TEXT, FormItemName.FONT_GRADUAL_FILL_DIRECTION],
            [FormItemName.FONT_GRADUAL_FILL_ANGLE_TEXT, FormItemName.FONT_GRADUAL_FILL_ANGLE],
            FormItemName.FONT_GRADUAL_FILL_BAR,
            FormItemName.FONT_GRADUAL_FILL_OPACITY,
            [FormItemName.FONT_IMAGE_FILL_UPLOAD, FormItemName.SPACE, FormItemName.FONT_IMAGE_FILL_CLIPBOARD],
            FormItemName.FONT_IMAGE_FILL_STYLE,
            FormItemName.FONT_IMAGE_FILL_OPACITY,
        ],
    },
    {
        label: FormLabelName[ActionType.FONT_BORDER],
        key: FormItemName.FONT_BORDER_PRESET,
        collapsable: true,
        items: [
            FormItemName.FONT_BORDER_SELECT,
            [
                FormItemName.FONT_BORDER_LINE, FormItemName.PUSHER,
                FormItemName.FONT_BORDER_WIDTH, FormItemName.FONT_BORDER_SPACE, FormItemName.FONT_BORDER_COLOR,
            ],
            FormItemName.FONT_BORDER_OPACITY,
            [
                FormItemName.FONT_BORDER_GRADUAL_TYPE_TEXT, FormItemName.PUSHER,
                FormItemName.FONT_BORDER_GRADUAL_LINEAR_GRADIENT, FormItemName.FONT_BORDER_GRADUAL_RADIAL_GRADIENT,
                FormItemName.FONT_BORDER_GRADUAL_RECT_GRADIENT, FormItemName.FONT_BORDER_GRADUAL_SHAPE_GRADIENT,
            ],
            [FormItemName.FONT_BORDER_GRADUAL_DIRECTION_TEXT, FormItemName.FONT_BORDER_GRADUAL_DIRECTION],
            [FormItemName.FONT_BORDER_GRADUAL_ANGLE_TEXT, FormItemName.FONT_BORDER_GRADUAL_ANGLE],
            FormItemName.FONT_BORDER_GRADUAL_BAR,
            FormItemName.FONT_BORDER_GRADUAL_OPACITY,
        ],
    },
    {
        label: FormLabelName[ActionType.FONT_SHADOW],
        key: FormItemName.FONT_SHADOW,
        items: [
            FormItemName.FONT_SHADOW_SIZE,
            FormItemName.FONT_SHADOW_BLUR,
            FormItemName.FONT_SHADOW_DISTANCE,
            FormItemName.FONT_SHADOW_OPACITY,
            [FormItemName.FONT_SHADOW_ANGLE, FormItemName.PUSHER, FormItemName.FONT_SHADOW_COLOR],
        ],
    },
    {
        label: FormLabelName[ActionType.FONT_REFLECTION],
        key: FormItemName.FONT_REFLECTION,
        items: [
            FormItemName.FONT_REFLECTION_SIZE,
            FormItemName.FONT_REFLECTION_BLUR,
            FormItemName.FONT_REFLECTION_DISTANCE,
            FormItemName.FONT_REFLECTION_OPACITY,
        ],
    },
    {
        label: FormLabelName[ActionType.FONT_GLOW],
        key: FormItemName.FONT_GLOW,
        items: [
            FormItemName.FONT_GLOW_SIZE,
            FormItemName.FONT_GLOW_OPACITY,
            FormItemName.FONT_GLOW_COLOR,
        ],
    },
];

const textFillAndBorderSchema = [
    {
        label: FormLabelName[ActionType.FONT_FILL],
        key: FormItemName.FONT_FILL_PRESET,
        items: [
            FormItemName.FONT_FILL_SELECT,
            [FormItemName.FONT_SOLID_FILL_COLOR_TEXT, FormItemName.FONT_SOLID_FILL_COLOR],
            FormItemName.FONT_SOLID_FILL_OPACITY,
            [
                FormItemName.FONT_GRADUAL_FILL_TYPE_TEXT, FormItemName.PUSHER,
                FormItemName.FONT_GRADUAL_FILL_LINEAR_GRADIENT, FormItemName.FONT_GRADUAL_FILL_RADIAL_GRADIENT,
                FormItemName.FONT_GRADUAL_FILL_RECT_GRADIENT, FormItemName.FONT_GRADUAL_FILL_SHAPE_GRADIENT,
            ],
            [FormItemName.FONT_GRADUAL_FILL_DIRECTION_TEXT, FormItemName.FONT_GRADUAL_FILL_DIRECTION],
            [FormItemName.FONT_GRADUAL_FILL_ANGLE_TEXT, FormItemName.FONT_GRADUAL_FILL_ANGLE],
            FormItemName.FONT_GRADUAL_FILL_BAR,
            FormItemName.FONT_GRADUAL_FILL_OPACITY,
            [FormItemName.FONT_IMAGE_FILL_UPLOAD, FormItemName.SPACE, FormItemName.FONT_IMAGE_FILL_CLIPBOARD],
            FormItemName.FONT_IMAGE_FILL_STYLE,
            FormItemName.FONT_IMAGE_FILL_OPACITY,
        ],
    },
    {
        label: FormLabelName[ActionType.FONT_BORDER],
        key: FormItemName.FONT_BORDER_PRESET,
        collapsable: true,
        items: [
            FormItemName.FONT_BORDER_SELECT,
            // [
            //     FormItemName.FONT_BORDER_LINE, FormItemName.PUSHER,
            //     FormItemName.FONT_BORDER_WIDTH, FormItemName.FONT_BORDER_SPACE, FormItemName.FONT_BORDER_COLOR,
            // ],
            [
                FormItemName.FONT_BORDER_GRADUAL_TYPE_TEXT, FormItemName.PUSHER,
                FormItemName.FONT_BORDER_GRADUAL_LINEAR_GRADIENT, FormItemName.FONT_BORDER_GRADUAL_RADIAL_GRADIENT,
                FormItemName.FONT_BORDER_GRADUAL_RECT_GRADIENT, FormItemName.FONT_BORDER_GRADUAL_SHAPE_GRADIENT,
            ],
            [FormItemName.FONT_BORDER_GRADUAL_DIRECTION_TEXT, FormItemName.FONT_BORDER_GRADUAL_DIRECTION],
            [FormItemName.FONT_BORDER_GRADUAL_ANGLE_TEXT, FormItemName.FONT_BORDER_GRADUAL_ANGLE],
            FormItemName.FONT_BORDER_GRADUAL_BAR,
            [FormItemName.FONT_BORDER_LINE_TEXT, FormItemName.FONT_BORDER_LINE],
            [FormItemName.FONT_BORDER_WIDTH_TEXT, FormItemName.FONT_BORDER_WIDTH],
            [FormItemName.FONT_BORDER_COLOR_TEXT, FormItemName.FONT_BORDER_COLOR],
            FormItemName.FONT_BORDER_OPACITY,
            FormItemName.FONT_BORDER_GRADUAL_OPACITY,
        ],
    },
];

const textEffectSchema = [
    {
        label: FormLabelName[ActionType.FONT_SHADOW],
        key: FormItemName.FONT_SHADOW,
        items: [
            [FormItemName.FONT_SHADOW_COLOR_TEXT, FormItemName.FONT_SHADOW_COLOR],
            FormItemName.FONT_SHADOW_SIZE,
            FormItemName.FONT_SHADOW_BLUR,
            FormItemName.FONT_SHADOW_DISTANCE,
            FormItemName.FONT_SHADOW_OPACITY,
            [FormItemName.FONT_SHADOW_ANGLE_TEXT, FormItemName.FONT_SHADOW_ANGLE],
        ],
    },
    {
        label: FormLabelName[ActionType.FONT_REFLECTION],
        key: FormItemName.FONT_REFLECTION,
        items: [
            FormItemName.FONT_REFLECTION_SIZE,
            FormItemName.FONT_REFLECTION_BLUR,
            FormItemName.FONT_REFLECTION_DISTANCE,
            FormItemName.FONT_REFLECTION_OPACITY,
        ],
    },
    {
        label: FormLabelName[ActionType.FONT_GLOW],
        key: FormItemName.FONT_GLOW,
        items: [
            [FormItemName.FONT_GLOW_COLOR_TEXT, FormItemName.FONT_GLOW_COLOR],
            FormItemName.FONT_GLOW_SIZE,
            FormItemName.FONT_GLOW_OPACITY,
        ],
    },
];

const textBoxSchema = [
    {
        label: FormLabelName.TEXTBOX,
        collapsable: true,
        items: [
            FormItemName.FONT_ADJUST_TEXT,
            FormItemName.FONT_ADJUST,
            FormItemName.TEXT_DIRECTION_LABEL,
            FormItemName.TEXT_DIRECTION,
            FormItemName.ORDER_OF_LINE_LABEL,
            FormItemName.ORDER_OF_LINE,
            [FormItemName.FONT_PADDING_TEXT, FormItemName.PUSHER, FormItemName.FONT_PADDING_UNIT],
            [FormItemName.FONT_PADDING_LEFT, FormItemName.SPACE, FormItemName.FONT_PADDING_RIGHT, FormItemName.SPACE,
                FormItemName.FONT_PADDING_TOP, FormItemName.SPACE, FormItemName.FONT_PADDING_BOTTOM],
            FormItemName.FONT_AUTO_BREAK,
        ],
    },
];


const tableTextSchema = [
    ...textSchema.slice(0, 2),
    {
        label: FormLabelName.TEXTBOX,
        collapsable: true,
        items: [
            FormItemName.TEXT_DIRECTION_LABEL,
            FormItemName.TEXT_DIRECTION,
            FormItemName.ORDER_OF_LINE_LABEL,
            FormItemName.ORDER_OF_LINE,
        ],
    },
    ...textSchema.slice(3),
];

const textFormSchema = {
    [FormPanelName.SHAPE]: {
        label: i18n.t('形状'),
        tabs: [
            {
                label: i18n.t('填充与边框'),
                sections: fillAndBorderSchema,
            },
            {
                label: i18n.t('效果'),
                sections: effectSchema,
            },
            {
                label: i18n.t('大小与位置'),
                sections: sizeAndPositionSchema,
            },
        ],
    },
    [FormPanelName.TEXT]: {
        label: i18n.t('文本'),
        tabs: [
            {
                label: i18n.t('填充与轮廓'),
                sections: textFillAndBorderSchema,
            },
            {
                label: i18n.t('效果'),
                sections: textEffectSchema,
            },
            {
                label: i18n.t('文本框'),
                sections: textBoxSchema,
            },
        ],
    },
};

const textImageFormSchema = {
    [FormPanelName.SHAPE]: {
        label: i18n.t('形状'),
        tabs: [
            {
                label: i18n.t('填充与边框'),
                sections: fillAndBorderSchema,
            },
            {
                label: i18n.t('效果'),
                sections: effectSchema,
            },
            {
                label: i18n.t('大小与位置'),
                sections: sizeAndPositionSchema,
            },
            {
                label: i18n.t('图片'),
                sections: pictureSchema,
            },
        ],
    },
    [FormPanelName.TEXT]: {
        label: i18n.t('文本'),
        tabs: [
            {
                label: i18n.t('填充与轮廓'),
                sections: textFillAndBorderSchema,
            },
            {
                label: i18n.t('效果'),
                sections: textEffectSchema,
            },
            {
                label: i18n.t('文本框'),
                sections: textBoxSchema,
            },
        ],
    },
};

const imageWithoutTextFormSchema = {
    [FormPanelName.SHAPE]: {
        label: i18n.t('形状'),
        tabs: [
            {
                label: i18n.t('填充与边框'),
                sections: fillAndBorderSchema,
            },
            {
                label: i18n.t('大小与位置'),
                sections: sizeAndPositionSchema,
            },
            {
                label: i18n.t('图片'),
                sections: pictureSchema,
            },
        ],
    },
};

const tablerBorderIdList = getBorderStyleData().map(item => item.id);

const tableSchema = [
    fillSchema,
    {
        label: FormLabelName[ActionType.BORDERSTYLE],
        collapsable: true,
        items: [
            [...tablerBorderIdList.slice(0, 5), FormItemName.BREAK, ...tablerBorderIdList.slice(5)],
            [FormItemName.TABLE_BORDER_COLOR_TEXT, FormItemName.TABLE_BORDER_COLOR],
        ],
    },
    {
        label: FormLabelName.CELL_SIZE,
        collapsable: true,
        items: [
            [FormItemName.CELL_HEIGHT, FormItemName.SPACE, FormItemName.CELL_WIDTH],
        ],
    },
];

const tableFormSchema = {
    [FormPanelName.SHAPE]: {
        label: i18n.t('形状'),
        tabs: [
            {
                label: i18n.t('填充与边框'),
                sections: [tableSchema[0], tableSchema[1]],
            },
            {
                label: i18n.t('大小与位置'),
                sections: sizeAndPositionSchema,
            },
        ],
    },
    [FormPanelName.TEXT]: {
        label: i18n.t('文本'),
        tabs: [
            {
                label: i18n.t('填充与轮廓'),
                sections: textFillAndBorderSchema,
            },
            {
                label: i18n.t('效果'),
                sections: textEffectSchema,
            },
            {
                label: i18n.t('文本框'),
                sections: [{
                    label: FormLabelName.TEXTBOX,
                    collapsable: true,
                    items: [
                        FormItemName.TEXT_DIRECTION_LABEL,
                        FormItemName.TEXT_DIRECTION,
                        FormItemName.ORDER_OF_LINE_LABEL,
                        FormItemName.ORDER_OF_LINE,
                    ],
                }],
            },
        ],
    },
};

const pictureFormSchema = {
    [FormPanelName.SHAPE]: {
        label: i18n.t('形状'),
        tabs: [
            {
                label: i18n.t('填充与边框'),
                sections: fillAndBorderSchema,
            },
            {
                label: i18n.t('效果'),
                sections: effectSchema,
            },
            {
                label: i18n.t('大小与位置'),
                sections: sizeAndPositionSchema,
            },
            {
                label: i18n.t('图片'),
                sections: pictureSchema,
            },
        ],
    },
};

const chartFormSchema = {
    ...SLIDE_FORM_SCHEMA,
};

const barChartScheam = CHART_BAR_SERIES_OPTIONS;
const pieDoughnutChartScheam = CHART_PIE_DOUGHNUT_SERIES_OPTIONS;
const bubbleChartScheam = CHART_BUBBLIE_SERIES_OPTIONS;

const smartArtFormSchema = {
    [FormPanelName.SHAPE]: {
        label: i18n.t('形状'),
        tabs: [
            {
                label: i18n.t('布局'),
                sections: layoutSchemaWithoutThreeDAndRotate,
            },
        ],
    },
};

const slideFormSchema = {
    [FormPanelName.SHAPE]: {
        label: i18n.t('形状'),
        tabs: [
            {
                label: i18n.t('幻灯片'),
                sections: [
                    {
                        label: FormLabelName.LAYOUT,
                        key: FormItemName.LAYOUT,
                        collapsable: true,
                        items: [
                            FormItemName.LAYOUT_CHANGE,
                        ],
                    },
                    {
                        label: FormLabelName.FOOTER,
                        key: FormItemName.FOOTER,
                        collapsable: true,
                        items: [
                            [FormItemName.SLIDE_NUMBER, FormItemName.SLIDE_NUMBER_START_TEXT,
                                FormItemName.SLIDE_NUMBER_START],
                            [FormItemName.SLIDE_FOOTER, FormItemName.SLIDE_FOOTER_INPUT],
                            [FormItemName.DATETIME, FormItemName.DATETIME_TYPE_SELECT],
                            [FormItemName.DATETIME_INPUT],
                            [FormItemName.DATETIME_FIELD_TYPE_SELECT],
                            FormItemName.FOOTER_APPLY,
                        ],
                    },
                    fillAndBorderSchema[0],
                ],
            },
        ],
    },
};

const masterFormSchema = {
    [FormPanelName.SHAPE]: {
        label: i18n.t('形状'),
        tabs: [
            {
                label: i18n.t('幻灯片'),
                sections: [
                    ...masterEditSchema,
                ],
            },
        ],
    },
};

const connecterFormSchema = {
    [FormPanelName.SHAPE]: {
        label: i18n.t('形状'),
        tabs: [
            {
                label: i18n.t('填充与边框'),
                sections: fillAndBorderSchema,
            },
            {
                label: i18n.t('大小与位置'),
                sections: sizeAndPositionSchema,
            },
        ],
    },
};

// 如多选图片和形状时展示的内容
const effectLayoutFormSchema = {
    [FormPanelName.SHAPE]: {
        label: i18n.t('形状'),
        tabs: [
            {
                label: i18n.t('填充与边框'),
                sections: fillAndBorderSchema,
            },
            {
                label: i18n.t('效果'),
                sections: effectSchema,
            },
            {
                label: i18n.t('大小与位置'),
                sections: sizeAndPositionSchema,
            },
        ],
    },
};

// 如第三方内容、视频等
const layoutFormSchema = {
    [FormPanelName.SHAPE]: {
        label: i18n.t('形状'),
        tabs: [
            {
                label: i18n.t('布局'),
                sections: layoutSchemaWithoutThreeD,
            },
        ],
    },
};

const layoutImageFormSchema = {
    [FormPanelName.SHAPE]: {
        label: i18n.t('形状'),
        tabs: [
            {
                label: i18n.t('布局'),
                sections: layoutSchemaWithoutThreeD,
            },
            {
                label: i18n.t('图片'),
                sections: pictureSchema,
            },
        ],
    },
};

const noteFormSchema = slideFormSchema;

export {
    textFormSchema,
    textImageFormSchema,
    pictureFormSchema,
    connecterFormSchema,
    tableFormSchema,
    slideFormSchema,
    masterFormSchema,
    layoutFormSchema,
    effectLayoutFormSchema,
    noteFormSchema,
    chartFormSchema,
    barChartScheam,
    pieDoughnutChartScheam,
    bubbleChartScheam,
    smartArtFormSchema,
    layoutImageFormSchema,
    imageWithoutTextFormSchema,
};
